import React, { createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import { evFacetClick, evFacetSearch } from 'events/search';
import { removeAutofacetFilter } from 'actions/autofacet';
import { getFacetUrlParams, getFacetUrlPath, toggleSelectedFacet, toggleSelectedFacetGroup } from 'actions/facets';
import { clearAutoComplete } from 'actions/autoComplete';
import { extractFacetGroup, findMatchingBreadcrumb } from 'helpers/AutofacetUtils';
import { isSingleSelectFacet } from 'components/search/FacetAccordion/FacetAccordion.utils';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { FEATURE_SEARCH_FACETS_UPDATE, FEATURE_SEARCH_FACETS_UPDATE_TWO } from 'constants/features';
import type { AppState } from 'types/app';
import type { AutoCompleteDataType, BreadcrumbType, FacetDataType, FacetSelectOptions, FacetValueType, NavigationVersion } from 'types/search';

export interface FacetsState {
  facets: FacetDataType[];
  autoComplete: AutoCompleteDataType;
  breadcrumbs: BreadcrumbType[];
  isLoading: boolean;
}

export interface FacetsActions {
  handleFacetSelect: (options: FacetSelectOptions) => void;
  handleAccordionChange: (expandedValues: string[]) => void;
  handleBreadcrumbRemoval: (breadcrumb: BreadcrumbType) => void;
  trackFacetSearch: (facetGroup: FacetDataType, searchText: string) => void;
}

export interface FacetsMeta {
  hasAutoComplete: boolean;
  navigationVersion: NavigationVersion;
}

export interface FacetsContextValue {
  state: FacetsState;
  actions: FacetsActions;
  meta: FacetsMeta;
}

const FacetsContext = createContext<FacetsContextValue | null>(null);

const useFacetsContext = () => {
  const context = useContext(FacetsContext);
  if (!context) {
    throw new Error('Facets components must be used within a FacetsProvider');
  }
  return context;
};

interface FacetsProviderProps {
  hasAutoComplete?: boolean;
  onFacetSelect?: (facetGroup: string, facetName: string, selectedFacetGroupIndex: number, selectedFacetIndex: number) => void;
  children: React.ReactNode;
}

/**
 * Reads facets state from Redux and provides
 * unified state/actions/meta to all facets UI components.
 */
const FacetsProvider = ({ hasAutoComplete = false, onFacetSelect, children }: FacetsProviderProps) => {
  const dispatch = useDispatch();
  const facets = useSelector((state: AppState) => state.facetsV2.navigationV2);
  const autoComplete = useSelector((state: AppState) => state.autoComplete);
  const breadcrumbs = useSelector((state: AppState) => state.filters.breadcrumbs);
  const isLoading = useSelector((state: AppState) => state.products.isLoading);
  const location = useSelector((state: AppState) => state.router.location);

  const { isEnrolled: isV2P2 } = useIsEnrolledInVariant(FEATURE_SEARCH_FACETS_UPDATE_TWO);
  const { isEnrolled: isV2 } = useIsEnrolledInVariant(FEATURE_SEARCH_FACETS_UPDATE);

  const navigationVersion: NavigationVersion = isV2P2 ? 'v2p2' : isV2 ? 'v2' : 'legacy';

  /**
   * Core workflow for applying any filter change: clear autocomplete, update URL, trigger search.
   */
  const applyFilterChange = (facetField: string, facetName: string, url: string) => {
    dispatch(clearAutoComplete(facetField));
    dispatch(getFacetUrlParams(url));
    dispatch(getFacetUrlPath(url));
    dispatch(toggleSelectedFacet(facetField, facetName, null, null, null));
  };

  /**
   * Removes an applied filter via breadcrumb pill.
   */
  const handleBreadcrumbRemoval = (breadcrumb: BreadcrumbType) => {
    const currentUrl = location.pathname + location.search;

    if (breadcrumb.removeUrl === currentUrl) {
      return;
    }

    const facetField = extractFacetGroup(breadcrumb.removeName);

    if (breadcrumb.autoFaceted) {
      dispatch(removeAutofacetFilter(breadcrumb));
    }

    applyFilterChange(facetField, breadcrumb.name, breadcrumb.removeUrl);

    track(() => [
      evFacetClick,
      {
        breadcrumbRemove: breadcrumb,
        facetClickSource: 'FACET_PILLS',
        selected: false,
        deselected: true
      }
    ]);
  };

  /**
   * Handles deselecting a facet by finding its breadcrumb for autofacet cleanup.
   */
  const handleDeselectFacet = (facetGroup: string, facetValue: FacetValueType) => {
    const matchingBreadcrumb = findMatchingBreadcrumb(facetGroup, facetValue.name, breadcrumbs);

    if (matchingBreadcrumb) {
      handleBreadcrumbRemoval(matchingBreadcrumb);
    } else {
      // Fallback: No breadcrumb found (shouldn't happen, but handle gracefully)
      const url = facetValue.facetUrl || facetValue.facetZsoUrl;
      if (url) {
        applyFilterChange(facetGroup, facetValue.name, url);
      }
    }
  };

  /**
   * Handles selecting a new facet.
   */
  const handleSelectFacet = (facetGroup: string, facetValue: FacetValueType) => {
    const url = facetValue.facetUrl || facetValue.facetZsoUrl;
    if (url) {
      applyFilterChange(facetGroup, facetValue.name, url);
    }
  };

  /**
   * Tracks analytics for facet interactions.
   */
  const trackFacetInteraction = (facetGroup: string, facetName: string, wasSelected: boolean) => {
    track(() => [
      evFacetClick,
      {
        facetGroup,
        facetName,
        facetClickSource: 'FACET_ACCORDION',
        selected: !wasSelected,
        deselected: wasSelected
      }
    ]);

    if (hasAutoComplete && autoComplete?.[facetGroup]?.text) {
      trackEvent('TE_SEARCH_AUTOCOMPLETE', `${facetGroup}:${autoComplete[facetGroup]?.text || ''}`);
    }
  };

  /**
   * Handles facet selection with analytics tracking and UI updates.
   *
   * Flow:
   * 1. Update filter state (select or deselect)
   * 2. Notify parent component
   * 3. Update UI (close accordion if needed)
   * 4. Track analytics
   * 5. Scroll to top
   */
  const handleFacetSelect = (options: FacetSelectOptions) => {
    const { facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isSelected, facetValue } = options;

    if (!facetValue) return;

    // Step 1: Update filter state
    if (isSelected) {
      handleDeselectFacet(facetGroup, facetValue);
    } else {
      handleSelectFacet(facetGroup, facetValue);
    }
    // Step 2: Notify parent component (legacy callback for special cases + analytics)
    onFacetSelect?.(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex);

    // Step 3: Close accordion for single-select facets after selection
    if (!isSelected && facets && navigationVersion !== 'v2p2') {
      const currentFacet = facets[selectedFacetGroupIndex];
      if (currentFacet && isSingleSelectFacet(currentFacet)) {
        dispatch(toggleSelectedFacetGroup(facetGroup));
      }
    }

    // Step 4: Track analytics
    trackFacetInteraction(facetGroup, facetName, isSelected);

    // Step 5: Scroll to top
    window.scrollTo(0, 0);
  };

  /**
   * Handles accordion expand/collapse state changes.
   */
  const handleAccordionChange = (expandedValues: string[]) => {
    if (!facets) return;

    const currentExpanded = facets.filter(facet => facet.isExpanded).map(facet => facet.facetField);
    const added = expandedValues.find(value => !currentExpanded.includes(value));
    const removed = currentExpanded.find(value => !expandedValues.includes(value));
    const toggledFacet = added || removed;

    if (toggledFacet) {
      dispatch(toggleSelectedFacetGroup(toggledFacet));
    }
  };

  /**
   * Debounced analytics event for facet search.
   */
  const trackFacetSearch = debounce((facetGroup: FacetDataType, searchText: string) => {
    track(() => [
      evFacetSearch,
      {
        autoComplete,
        facetGroup,
        term: searchText
      }
    ]);
  }, 1000);

  return (
    <FacetsContext.Provider
      value={{
        state: { facets, autoComplete, breadcrumbs, isLoading },
        actions: { handleFacetSelect, handleAccordionChange, handleBreadcrumbRemoval, trackFacetSearch },
        meta: { hasAutoComplete, navigationVersion }
      }}
    >
      {children}
    </FacetsContext.Provider>
  );
};

export { FacetsProvider, useFacetsContext, FacetsContext };
